/**
 * usePRD Hook
 *
 * TanStack Query hooks for fetching and managing PRDs
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  PRD,
  PRDResponse,
  PRDListResponse,
  GeneratePRDRequest,
  UpdatePRDRequest,
} from '@forgenetwork/types';

/**
 * Fetch a single PRD by ID
 *
 * @param prdId - The PRD ID
 * @param enabled - Whether the query is enabled (default: true)
 * @returns Query result with PRD data
 *
 * @example
 * ```tsx
 * function PRDViewer({ prdId }: { prdId: string }) {
 *   const { data, isLoading } = usePRD(prdId);
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return <div>{data?.prd.title}</div>;
 * }
 * ```
 */
export function usePRD(prdId: string, enabled = true) {
  return useQuery<PRDResponse>({
    queryKey: ['prd', prdId],
    queryFn: async () => {
      return await api.get<PRDResponse>(`/prd/${prdId}`);
    },
    enabled: enabled && !!prdId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Fetch all PRDs for a repository
 *
 * @param repositoryId - The repository ID
 * @param enabled - Whether the query is enabled (default: true)
 * @returns Query result with PRD list
 *
 * @example
 * ```tsx
 * function PRDList({ repositoryId }: { repositoryId: string }) {
 *   const { data, isLoading } = usePRDList(repositoryId);
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return (
 *     <div>
 *       {data?.prds.map(prd => (
 *         <PRDCard key={prd.id} prd={prd} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function usePRDList(repositoryId: string, enabled = true) {
  return useQuery<PRDListResponse>({
    queryKey: ['prds', repositoryId],
    queryFn: async () => {
      return await api.get<PRDListResponse>(`/repositories/${repositoryId}/prd`);
    },
    enabled: enabled && !!repositoryId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Mutation to generate a new PRD from an analysis
 *
 * @returns Mutation object with mutate function
 *
 * @example
 * ```tsx
 * function GeneratePRDButton({ repoId, analysisId }: Props) {
 *   const generatePRD = useGeneratePRD();
 *
 *   return (
 *     <button
 *       onClick={() => generatePRD.mutate({ repoId, analysisId })}
 *       disabled={generatePRD.isPending}
 *     >
 *       {generatePRD.isPending ? 'Generating...' : 'Generate PRD'}
 *     </button>
 *   );
 * }
 * ```
 */
export function useGeneratePRD() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      repoId,
      analysisId,
      template,
    }: {
      repoId: string;
      analysisId?: string;
      template?: 'standard' | 'minimal' | 'detailed';
    }) => {
      // analysisId is optional - backend will use latest completed analysis if not provided
      const request: Partial<GeneratePRDRequest> = { template };
      if (analysisId) {
        request.analysisId = analysisId;
      }
      return await api.post<PRDResponse>(
        `/repositories/${repoId}/prd/generate`,
        request
      );
    },
    onSuccess: (_data, variables) => {
      // Invalidate PRD list cache to refetch
      queryClient.invalidateQueries({
        queryKey: ['prds', variables.repoId],
      });
      // Also invalidate repository cache
      queryClient.invalidateQueries({
        queryKey: ['repository', variables.repoId],
      });
    },
  });
}

/**
 * Mutation to update an existing PRD
 *
 * @returns Mutation object with mutate function
 *
 * @example
 * ```tsx
 * function PRDEditor({ prdId }: { prdId: string }) {
 *   const updatePRD = useUpdatePRD();
 *
 *   const handleSave = (updates: UpdatePRDRequest) => {
 *     updatePRD.mutate({ prdId, updates });
 *   };
 *
 *   return <form onSubmit={handleSave}>...</form>;
 * }
 * ```
 */
export function useUpdatePRD() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      prdId,
      updates,
    }: {
      prdId: string;
      updates: UpdatePRDRequest;
    }) => {
      return await api.patch<PRDResponse>(`/prd/${prdId}`, updates);
    },
    onSuccess: (data) => {
      // Invalidate specific PRD cache
      queryClient.invalidateQueries({
        queryKey: ['prd', data.prd.id],
      });
      // Invalidate PRD list for this repository
      queryClient.invalidateQueries({
        queryKey: ['prds', data.prd.repositoryId],
      });
    },
  });
}

/**
 * Mutation to delete a PRD
 *
 * @returns Mutation object with mutate function
 *
 * @example
 * ```tsx
 * function DeletePRDButton({ prdId, repoId }: Props) {
 *   const deletePRD = useDeletePRD();
 *
 *   return (
 *     <button
 *       onClick={() => deletePRD.mutate({ prdId, repoId })}
 *       disabled={deletePRD.isPending}
 *     >
 *       Delete PRD
 *     </button>
 *   );
 * }
 * ```
 */
export function useDeletePRD() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ prdId }: { prdId: string; repoId: string }) => {
      return await api.delete<void>(`/prd/${prdId}`);
    },
    onSuccess: (_data, variables) => {
      // Invalidate PRD list cache
      queryClient.invalidateQueries({
        queryKey: ['prds', variables.repoId],
      });
      // Remove from specific PRD cache
      queryClient.removeQueries({
        queryKey: ['prd', variables.prdId],
      });
    },
  });
}

/**
 * Get cached PRD without triggering a fetch
 *
 * @param prdId - The PRD ID
 * @returns Cached PRD or undefined
 *
 * @example
 * ```tsx
 * function QuickPRDTitle({ prdId }: { prdId: string }) {
 *   const prd = useCachedPRD(prdId);
 *
 *   return <span>{prd?.title ?? 'Loading...'}</span>;
 * }
 * ```
 */
export function useCachedPRD(prdId: string): PRD | undefined {
  const queryClient = useQueryClient();
  const cached = queryClient.getQueryData<PRDResponse>(['prd', prdId]);
  return cached?.prd;
}

/**
 * Get cached PRD list without triggering a fetch
 *
 * @param repositoryId - The repository ID
 * @returns Cached PRDs or undefined
 *
 * @example
 * ```tsx
 * function QuickPRDCount({ repositoryId }: { repositoryId: string }) {
 *   const prds = useCachedPRDList(repositoryId);
 *
 *   return <span>{prds?.length ?? 0} PRDs</span>;
 * }
 * ```
 */
export function useCachedPRDList(repositoryId: string): PRD[] | undefined {
  const queryClient = useQueryClient();
  const cached = queryClient.getQueryData<PRDListResponse>(['prds', repositoryId]);
  return cached?.prds;
}

/**
 * Export format types
 */
export type ExportFormat = 'markdown' | 'json' | 'html';

/**
 * Hook to export PRD in various formats
 *
 * @returns Mutation object with mutate function that triggers download
 *
 * @example
 * ```tsx
 * function ExportButton({ prdId }: { prdId: string }) {
 *   const exportPRD = useExportPRD();
 *
 *   return (
 *     <button onClick={() => exportPRD.mutate({ prdId, format: 'markdown' })}>
 *       Export as Markdown
 *     </button>
 *   );
 * }
 * ```
 */
export function useExportPRD() {
  return useMutation({
    mutationFn: async ({
      prdId,
      format = 'markdown',
    }: {
      prdId: string;
      format?: ExportFormat;
    }) => {
      // Fetch the export as a download
      const response = await fetch(`/api/v1/prd/${prdId}/export?format=${format}`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to export PRD');
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
      const extension = format === 'json' ? 'json' : format === 'html' ? 'html' : 'md';
      const filename = filenameMatch?.[1] || `prd-export.${extension}`;

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      return { filename };
    },
  });
}
